import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Textarea from 'react-textarea-autosize';
import { trackEvent } from '@medflex/utils/Tracking';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import SessionsFormField from '../SessionsFormField';
import Button from '../../Button';
import TextSnippetsPopup from '../../TextSnippetsPopup';
import Tooltip from '../../Tooltip';

const MAX_DESCRIPTION_LENGTH = 400;

const Description = ({
  control,
  defaultValue = '',
  error,
  setValue,
  snippets,
  showWarning,
  watch,
}) => {
  const { t } = useTranslation('components');
  const [showTextInput, setShowTextInput] = useState(defaultValue);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const textRef = useRef();
  const descriptionValue = watch('description');

  const saveSelection = event => {
    setSelectionStart(event.target.selectionStart);
    setSelectionEnd(event.target.selectionEnd);
  };

  const handleInsert = ({ id }) => {
    const { text } = snippets.find(sn => sn.id === id);
    const value = descriptionValue
      ? `${descriptionValue.slice(0, selectionStart)}${text}${descriptionValue.slice(
          selectionEnd,
          descriptionValue.length,
        )}`
      : text;
    setValue('description', value);

    if (textRef.current) {
      setTimeout(() => {
        textRef.current.setSelectionRange(
          selectionStart + text.length,
          selectionStart + text.length,
        );
        textRef.current.focus();
      });
    }
  };

  const handleOpen = event => {
    trackEvent(event);
    setShowTextInput(true);
  };

  return (
    <div data-component="SessionsScheduleForm__Description">
      {!showTextInput ? (
        <Button className="flex items-center space-x-2" onClick={handleOpen} kind="link">
          <PlusCircleIcon className="w-7 h-7" />
          <div>{t('Sessions.SessionsScheduleForm.Description.button', { allowHtml: true })}</div>
        </Button>
      ) : (
        <SessionsFormField
          className="flex-grow"
          label={t('Sessions.SessionsScheduleForm.Description.label', { allowHtml: true })}
          error={error}
        >
          <Controller
            control={control}
            defaultValue={defaultValue}
            name="description"
            render={({ name, onChange, value }) => (
              <Textarea
                className="p-3 resize-none border border-slate-500 focus:border-blue-500 rounded outline-none placeholder-slate-500 leading-tight w-full min-h-28"
                id={name}
                ref={textRef}
                maxLength={MAX_DESCRIPTION_LENGTH}
                minRows={2}
                name={name}
                onBlur={saveSelection}
                onChange={val => onChange(val)}
                value={value}
              />
            )}
          />

          {showWarning && (
            <div className="text-sm text-slate-600">
              {t('Sessions.SessionsScheduleForm.Description.warning')}
            </div>
          )}

          <Tooltip title={t('Sessions.SessionsScheduleForm.Description.tooltip')}>
            <div className="inline">
              <TextSnippetsPopup snippets={snippets} onClick={handleInsert} />
            </div>
          </Tooltip>
        </SessionsFormField>
      )}
    </div>
  );
};

Description.propTypes = {
  watch: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string,
    type: PropTypes.string,
  }),
  snippets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      id: PropTypes.string,
    }),
  ),
  setValue: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  showWarning: PropTypes.bool,
};

Description.defaultProps = {
  snippets: [],
};

export default Description;
